import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { mockData } from '../data/mock';

const InvestmentPage = () => {
  const [investmentData, setInvestmentData] = useState({});

  useEffect(() => {
    setInvestmentData(mockData.investment);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/9iuduyck_Image%2061.jpeg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/70 to-orange-900/60"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge variant="outline" className="text-amber-300 border-amber-300 mb-4 bg-white/10 backdrop-blur-sm">
              Investment Opportunity
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg">
              The National Memorial Garden
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              A unique £3 million investment opportunity in the growing memorial services sector. 
              Strategic location, innovative design, and experienced management team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 text-lg shadow-xl">
                  Request Investment Information
                </Button>
              </Link>
              <Link to="/masterplan">
                <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/20 backdrop-blur-sm px-8 py-3 text-lg">
                  View Project Details
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Overview */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Investment Highlights</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {investmentData.opportunity}
            </p>
          </div>

          {investmentData.projections && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="text-center p-8 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {investmentData.projections.investment}
                </div>
                <div className="text-emerald-100 text-sm md:text-base font-medium">Total Investment</div>
              </div>
              <div className="text-center p-8 bg-gradient-to-br from-amber-600 to-orange-600 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {investmentData.projections.annualRevenue}
                </div>
                <div className="text-amber-100 text-sm md:text-base font-medium">Annual Revenue*</div>
              </div>
              <div className="text-center p-8 bg-gradient-to-br from-purple-600 to-violet-600 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {investmentData.projections.projectedProfit}
                </div>
                <div className="text-purple-100 text-sm md:text-base font-medium">Projected Profit*</div>
              </div>
            </div>
          )}

          <div className="text-center">
            <p className="text-sm text-gray-500">*Projected figures based on market analysis and existing facility performance</p>
          </div>
        </div>
      </section>

      {/* Design Excellence Showcase */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Exceptional Design Quality</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our memorial gardens represent the pinnacle of contemporary landscape architecture 
              and thoughtful design, creating beautiful spaces for remembrance and reflection.
            </p>
          </div>

          <div className="space-y-12">
            {/* First Image - Full Width */}
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/bv6r0glt_Picture%201.jpg" 
                alt="Memorial Garden with Spring Blossoms" 
                className="w-full h-96 md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 drop-shadow-lg">
                  Seasonal Memorial Gardens
                </h3>
                <p className="text-white/90 text-lg leading-relaxed max-w-2xl">
                  Beautiful spring blossoms create spaces of hope and renewal, with contemporary memorial elements 
                  integrated seamlessly into the natural landscape.
                </p>
              </div>
            </div>

            {/* Second and Third Images - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/9pkw8uio_Image%2060.jpeg" 
                  alt="Modern Memorial Architecture" 
                  className="w-full h-80 md:h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 drop-shadow-lg">
                    Contemporary Architecture
                  </h3>
                  <p className="text-white/90 text-sm md:text-base leading-relaxed">
                    Modern design meets natural landscaping with innovative use of materials and thoughtful integration with the environment.
                  </p>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/9iuduyck_Image%2061.jpeg" 
                  alt="Autumn Reflections Memorial Garden" 
                  className="w-full h-80 md:h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 drop-shadow-lg">
                    Four Seasons Experience
                  </h3>
                  <p className="text-white/90 text-sm md:text-base leading-relaxed">
                    Year-round beauty with stunning autumn colors reflected in tranquil water features, 
                    creating moments of peaceful contemplation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              Every element is carefully designed to create spaces that honor life and provide comfort to families.
            </p>
            <Link to="/masterplan">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3">
                Explore Our Complete Vision
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/b1q8t6pz_Picture%201.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/85 to-emerald-900/75"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Why This Investment?</h2>
          </div>

          {investmentData.highlights && (
            <div className="space-y-8">
              {/* First 4 items in 2x2 grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {investmentData.highlights.slice(0, 4).map((highlight, index) => (
                  <Card key={index} className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm hover:bg-white transition-all duration-300 transform hover:scale-105">
                    <CardContent className="p-8">
                      <div>
                        <p className="text-gray-700 leading-relaxed text-lg">{highlight}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* 5th item centered */}
              {investmentData.highlights.length > 4 && (
                <div className="flex justify-center">
                  <div className="w-full md:w-1/2">
                    <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm hover:bg-white transition-all duration-300 transform hover:scale-105">
                      <CardContent className="p-8">
                        <div>
                          <p className="text-gray-700 leading-relaxed text-lg">{investmentData.highlights[4]}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Market Position */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Add image showcase */}
          <div className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/qxpttghu_Picture%203.jpg" 
                  alt="Modern Chapel Interior" 
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="text-white text-xl font-bold drop-shadow-lg">Contemporary Chapel Design</h4>
                  <p className="text-white/90 text-sm mt-1">Modern facilities designed for comfort and reflection</p>
                </div>
              </div>
              
              <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
                <img 
                  src="https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/8y5oakel_Image%2059.jpeg" 
                  alt="Memorial Building with Warm Lighting" 
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="text-white text-xl font-bold drop-shadow-lg">Architectural Excellence</h4>
                  <p className="text-white/90 text-sm mt-1">Award-winning design meets functional beauty</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white transform hover:scale-105 transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-4">Market Demand</h3>
                <p className="text-emerald-100 leading-relaxed">
                  78% cremation rate in the UK with increasing demand for alternative memorial options 
                  and beautiful spaces for remembrance.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white transform hover:scale-105 transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-4">Proven Experience</h3>
                <p className="text-amber-100 leading-relaxed">
                  15+ years of experience with existing successful facility operation, 
                  demonstrating our ability to deliver exceptional results.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-xl bg-gradient-to-br from-purple-500 to-violet-600 text-white transform hover:scale-105 transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold mb-4">Strategic Location</h3>
                <p className="text-purple-100 leading-relaxed">
                  Prime location near M11, M25, and M1 motorways providing unprecedented 
                  UK-wide accessibility for national market reach.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/9iuduyck_Image%2061.jpeg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/85 to-emerald-900/75"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">Interested in Learning More?</h2>
          <p className="text-xl mb-8 text-white/90">
            For detailed investment information, financial projections, and partnership opportunities, 
            please get in touch with our team.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="font-semibold mb-2 text-white">Phone</h3>
              <a href="tel:020 3576 3018" className="text-amber-300 hover:text-white transition-colors text-lg">
                020 3576 3018
              </a>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="font-semibold mb-2 text-white">Email</h3>
              <a href="mailto:info@fenixpartnership.co.uk" className="text-amber-300 hover:text-white transition-colors text-lg">
                info@fenixpartnership.co.uk
              </a>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 shadow-xl">
                Contact Us Today
              </Button>
            </Link>
            <Link to="/masterplan">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/20 backdrop-blur-sm px-8 py-3">
                View Project Details
              </Button>
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20 text-sm text-white/75">
            <p>This information is for qualified investors only. Past performance does not guarantee future results.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestmentPage;